/**
 * Talk BI KPI tile, pivot and flat table (spec §7, the approved comp). Card-less: the caller's card
 * holds them. Props are plain data; nothing fetches. `format` is a bi-format kind:
 * 'money' | 'qty' | 'pct' | 'share'. A change reads as text colour AND an arrow with a name, never colour alone.
 */
import { pivotize, changeOf, heatPct, barPct, sparkPath, dimLabels, rowLabels, sortRows } from './bi-tables.mjs';
import { formatter, toNumber } from './bi-charts.mjs';
import { cell, qty, share } from './bi-format.mjs';

export { pivotize };

const { useState } = React;
const DASH = '—';
const num = (text) => <bdi dir="ltr">{text}</bdi>; // "-₪5M" must not read "₪5M-" in RTL

function Arrow({ dir, lang }) {
  const he = lang === 'he';
  const name = dir === 'up' ? (he ? 'עלייה' : 'Up') : dir === 'down' ? (he ? 'ירידה' : 'Down') : (he ? 'ללא שינוי' : 'No change');
  return (
    <svg className="bi-delta-icon" viewBox="0 0 10 10" role="img" aria-label={name} focusable="false">
      {dir === 'flat' ? <circle cx="5" cy="5" r="2.5"/> : <path d={dir === 'up' ? 'M5 1.5 9.5 9h-9z' : 'M5 8.5.5 1h9z'}/>}
    </svg>
  );
}

// change: changeOf's {pct, dir}. good: true = up is good, false = up is bad, null = neutral.
function Delta({ change, good = true, lang }) {
  const tone = good === null || change.dir === 'flat' ? 'flat' : (change.dir === 'up') === good ? 'good' : 'bad';
  return (
    <span className="bi-delta" data-tone={tone}>
      <Arrow dir={change.dir} lang={lang}/>
      {num(share(Math.abs(change.pct)))}
    </span>
  );
}

const changeCell = (a, b, good, lang) => {
  const ch = changeOf(a, b);
  return ch === DASH ? DASH : <Delta change={ch} good={good} lang={lang}/>;
};

// value / compareValue: numbers (or the runner's Decimal strings). spark: the period's values, oldest first.
// compared: false when no previous period was asked for, so the tile says nothing about one (a report KPI).
export function KpiTile({ label, value, compareValue = null, format = 'qty', spark = null, good = true, emptyReason = null, compared = true, lang }) {
  const he = lang === 'he';
  const short = formatter(format), full = formatter(format, false);
  const v = emptyReason ? null : toNumber(value);
  const prev = toNumber(compareValue);
  const ch = changeOf(v, prev);
  const path = v !== null && spark ? sparkPath(spark) : null;
  let ref;
  if (emptyReason) ref = <span dir="auto">{emptyReason}</span>; // the server's words: their own direction
  else if (!compared) ref = null;
  else if (prev === null) ref = <span>{he ? 'אין נתונים לתקופה הקודמת' : 'No data for the previous period'}</span>;
  else ref = <>{ch !== DASH && <Delta change={ch} good={good} lang={lang}/>}<span>{he ? 'מול' : 'vs'} {num(short(prev))}</span></>;
  return (
    <div className="bi-kpi">
      <p className="bi-kpi-label">{label}</p>
      <p className="bi-kpi-value" title={v === null ? undefined : full(v)}>{num(v === null ? DASH : short(v))}</p>
      {ref && <p className="bi-kpi-ref">{ref}</p>}
      {path && (
        <svg className="bi-spark" viewBox="0 0 120 36" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path className="bi-spark-area" d={path.area}/>
          <path className="bi-spark-line" d={path.line}/>
        </svg>
      )}
    </div>
  );
}

const heatStyle = (pct) => (pct > 0 ? { backgroundColor: `color-mix(in srgb, var(--bi-heat) ${pct}%, transparent)` } : undefined);
// a data bar grows from the reading start: the right edge in Hebrew
const barStyle = (pct, rtl) => (pct > 0
  ? { backgroundImage: `linear-gradient(to ${rtl ? 'left' : 'right'}, color-mix(in srgb, var(--bi-heat) 20%, transparent) ${pct}%, transparent 0)` }
  : undefined);

/**
 * rows: the row dims' titles (the header of the row-label column); cols: the column dim's title.
 * cells: pivotize(result). totals / heat: the definition's switches. compare: {label, cells: pivotize(previous)}
 * adds the previous period's column and a change column. onRowPick(rowKey) gets the row's dim values as an
 * array, or null when the selected row is picked again; selected is that array. good colours the change
 * column as KpiTile does: true = up is good, false = down is good (a cost), null = neutral.
 */
export function PivotTable({ rows: rowTitles, cols: colTitle, cells: p, totals = true, compare = null, heat = true, onRowPick, selected = null, format = 'qty', good = true, lang }) {
  const he = lang === 'he';
  const rtl = he;
  const f = (v) => num(cell(v, format));
  const rowText = rowLabels(p, lang);
  const colText = p.colDim ? dimLabels(p.colKeys, p.colDim, lang) : [];
  const maxCell = Math.max(0, ...p.rowKeys.flatMap((rk) => p.colKeys.map((c) => p.cell(rk, c))).filter(Number.isFinite));
  const maxRow = Math.max(0, ...p.rowKeys.map(p.rowTotal).filter(Number.isFinite));
  const prev = compare?.cells ?? null;
  const showTotals = totals && !p.truncated;
  const picked = selected == null ? null : JSON.stringify(selected);
  const corner = (rowTitles ?? p.rowDims).join(' · ');
  const totalWord = he ? 'סה״כ' : 'Total';
  return (
    <div className="bi-chart">
      <div className="tbl-wrap bi-table-scroll" tabIndex={0} role="region" aria-label={colTitle ? `${corner} × ${colTitle}` : corner}>
        <table className="bi-table">
          <thead>
            <tr>
              <th scope="col">{corner}</th>
              {colText.map((t, i) => <th scope="col" className="bi-num" key={JSON.stringify(p.colKeys[i])}>{t}</th>)}
              <th scope="col" className="bi-num">{totalWord}</th>
              {prev && <th scope="col" className="bi-num">{compare.label ?? (he ? 'התקופה הקודמת' : 'Previous period')}</th>}
              {prev && <th scope="col" className="bi-num">{he ? 'שינוי' : 'Change'}</th>}
            </tr>
          </thead>
          <tbody>
            {p.rowKeys.map((rk, i) => {
              const id = JSON.stringify(rk);
              const t = p.rowTotal(rk);
              return (
                <tr key={id}>
                  <th scope="row">
                    {onRowPick
                      ? <button type="button" className="bi-row-pick" aria-pressed={id === picked} onClick={() => onRowPick(id === picked ? null : rk)}><bdi>{rowText[i]}</bdi></button>
                      : <bdi>{rowText[i]}</bdi>}
                  </th>
                  {p.colKeys.map((c) => {
                    const v = p.cell(rk, c);
                    return <td className="bi-num" key={JSON.stringify(c)} style={heat ? heatStyle(heatPct(v, maxCell)) : undefined}>{f(v)}</td>;
                  })}
                  <td className="bi-num bi-total" style={barStyle(barPct(t, maxRow), rtl)}>{f(t)}</td>
                  {prev && <td className="bi-num">{f(prev.rowTotal(rk))}</td>}
                  {prev && <td className="bi-num">{changeCell(t, prev.rowTotal(rk), good, lang)}</td>}
                </tr>
              );
            })}
          </tbody>
          {showTotals && (
            <tfoot>
              <tr>
                <th scope="row">{totalWord}</th>
                {p.colKeys.map((c) => <td className="bi-num" key={JSON.stringify(c)}>{f(p.colTotal(c))}</td>)}
                <td className="bi-num">{f(p.grand)}</td>
                {prev && <td className="bi-num">{f(prev.grand)}</td>}
                {prev && <td className="bi-num">{changeCell(p.grand, prev.grand, good, lang)}</td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {p.truncated && (
        <p className="bi-foot">
          {he ? `מוצגים ${qty(p.truncated.shown)} תאים מתוך ${qty(p.truncated.of)}` : `Showing ${qty(p.truncated.shown)} of ${qty(p.truncated.of)} cells`}
        </p>
      )}
    </div>
  );
}

/**
 * columns: [{key, label, measure?, format?}]. A measure column is numeric: end-aligned, sorted by value and
 * shown with cell(v, column.format ?? format), so a 0 or blank reads '—'. Any other column is text.
 * rows: plain objects keyed by column key. sortable: each header is a button and its th carries aria-sort;
 * the sort reorders only the rows given (a truncated result sorts its first rows, not the whole set).
 * barColumn: a measure column's key, drawn with data bars.
 */
export function FlatTable({ columns, rows, sortable = false, barColumn = null, format = 'qty', lang }) {
  const he = lang === 'he';
  const [sort, setSort] = useState(null);
  const shown = sort ? sortRows(rows, sort.key, sort.dir, Boolean(columns.find((c) => c.key === sort.key)?.measure), lang) : rows;
  const barMax = barColumn ? Math.max(0, ...rows.map((r) => toNumber(r[barColumn])).filter(Number.isFinite)) : 0;
  const flip = (col) => setSort((s) => ({
    key: col.key,
    dir: s?.key === col.key ? (s.dir === 'ascending' ? 'descending' : 'ascending') : col.measure ? 'descending' : 'ascending',
  }));
  return (
    <div className="bi-chart">
      <div className="tbl-wrap bi-table-scroll bi-flat-scroll" tabIndex={0} role="region" aria-label={columns.map((c) => c.label).join(' · ')}>
        <table className="bi-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const dir = sort?.key === col.key ? sort.dir : null;
                return (
                  <th scope="col" key={col.key} className={col.measure ? 'bi-num' : undefined} aria-sort={sortable ? dir ?? 'none' : undefined}>
                    {sortable
                      ? (
                        <button type="button" className="bi-sort" onClick={() => flip(col)}>
                          {col.label}
                          {dir && <svg className="bi-sort-icon" viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d={dir === 'ascending' ? 'M5 2 9 8H1z' : 'M5 8 1 2h8z'}/></svg>}
                        </button>
                      )
                      : col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>
                {columns.map((col) => {
                  const v = r[col.key];
                  if (!col.measure) return <td key={col.key}><bdi>{v == null || v === '' ? DASH : String(v)}</bdi></td>;
                  const n = toNumber(v);
                  return (
                    <td key={col.key} className="bi-num" style={col.key === barColumn ? barStyle(barPct(n, barMax), he) : undefined}>
                      {num(cell(n, col.format ?? format))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
